import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import organizationalUnitsService from '../services/organizationalUnitsService';
import {OrganizationalUnit} from '../types/OrganizationalUnit';
import {DataTable, DataTableFilterMeta} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Button} from 'primereact/button';
import {useNavigate, useSearchParams} from "react-router-dom";
import AdminFormHeader from "../../../layout/admin/AdminFormHeader";
import {AdminButton} from "../../../layout/admin";
import {FilterMatchMode} from "primereact/api";
import {DEFAULT_ORGANIZATIONAL_UNIT_PRESET, useOrganizationalUnitPresets} from "../constants/presets";
import {handleError} from "../../../common/utils/errorHandling";
import {useToast} from "../../../common/contexts/ToastContext";
import {useAdminConfirmDialog} from "../../../common/hooks/useAdminConfirmDialog";

const OrganizationalUnitsList: React.FC = () => {
  const {t} = useTranslation();
  const {showError, showSuccess} = useToast();
  const {showConfirmDialog} = useAdminConfirmDialog();
  const presets = useOrganizationalUnitPresets();
  const [units, setUnits] = useState<OrganizationalUnit[]>([]);
  const [activePreset, setActivePreset] = useState<string>(DEFAULT_ORGANIZATIONAL_UNIT_PRESET);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    name: {value: null, matchMode: FilterMatchMode.CONTAINS},
    type: {value: null, matchMode: FilterMatchMode.CONTAINS},
    'parent.name': {value: null, matchMode: FilterMatchMode.CONTAINS},
    hierarchyType: {value: null, matchMode: FilterMatchMode.EQUALS}
  });
  const preset = searchParams.get('preset') || DEFAULT_ORGANIZATIONAL_UNIT_PRESET;

  useEffect(() => {
    handlePresetChange(preset);
    fetchUnits();
  }, []);

  const fetchUnits = async (): Promise<void> => {
    const unitsData = await organizationalUnitsService.getAllOrganizationalUnits();
    setUnits(unitsData);
  };

  const handleDelete = async (unit: OrganizationalUnit): Promise<void> => {
    showConfirmDialog({
      message: t('admin.common.messages.confirmDeleteWithName', {name: unit.name}),
      accept: async () => {
        await handleError(
          async () => {
            await organizationalUnitsService.deleteOrganizationalUnit(unit.id);
            await fetchUnits();
          },
          t('admin.organizationalUnits.errors.deleting'),
          undefined,
          showError
        );
      }
    });
  };

  const handleEdit = (unit: OrganizationalUnit) => {
    navigate(`${unit.id}/edit`);
  };

  const handleCreateNew = () => {
    navigate(`new?preset=${activePreset}`);
  };

  const handlePresetChange = (alias: string) => {
    setActivePreset(alias);
    setFilters(prev => ({
      ...prev,
      hierarchyType: {value: alias, matchMode: FilterMatchMode.EQUALS}
    }));
  };

  return (
    <>
      <AdminFormHeader
        title={t('admin.organizationalUnits.title.list')}
        subtitle={t('admin.organizationalUnits.subtitle.list')}
        presets={presets}
        activePreset={activePreset}
        onPresetChange={handlePresetChange}
      />

      <AdminButton
        onClick={handleCreateNew}
        variant="black"
        label={t('admin.organizationalUnits.actions.createNew')}
      />

      <DataTable
        value={units}
        stripedRows
        paginator
        rows={10}
        filterDisplay="row"
        filters={filters}
        columnResizeMode="fit"
        resizableColumns
        showGridlines
      >
        <Column
          sortField="name"
          header={t('admin.common.columns.name')}
          sortable
          filter
          filterField="name"
          filterPlaceholder={t('admin.common.filters.searchByName')}
          showFilterMenu={false}
          showClearButton={false}
          body={(unit: OrganizationalUnit) => (
            <strong>{unit.name}</strong>
          )}
        />
        <Column
          sortField="type"
          header={t('admin.common.columns.type')}
          sortable
          filter
          filterField="type"
          filterPlaceholder={t('admin.common.filters.searchByType')}
          showFilterMenu={false}
          showClearButton={false}
          body={(unit: OrganizationalUnit) => (
            <span>{unit.type}</span>
          )}
        />
        <Column
          sortField="parent.name"
          header={t('admin.organizationalUnits.columns.parent')}
          sortable
          filter
          filterField="parent.name"
          filterPlaceholder={t('admin.organizationalUnits.placeholders.searchByParent')}
          showFilterMenu={false}
          showClearButton={false}
          body={(unit: OrganizationalUnit) => (
            <span>{unit.parent?.name}</span>
          )}
        />
        <Column header={t('admin.common.columns.actions')} body={(unit: OrganizationalUnit) => (
          <div>
            <Button
              onClick={() => handleEdit(unit)}
              icon="fa fa-edit"
              severity="info"
              size="small"
              outlined
              className="mr-2"
            />
            <Button
              onClick={() => handleDelete(unit)}
              icon="fa fa-trash"
              severity="danger"
              size="small"
              outlined
            />
          </div>)}/>
      </DataTable>
    </>
  );
};

export default OrganizationalUnitsList;

